// Helpers of the admin page /groups (list + editor): editor form <-> SaveParticipantGroupRequest,
// client-side validation (mirrors ParticipantGroupService.Validate + entity max lengths), labels.
import { formatEuro, parsePriceInput, samePrice, toPriceInput } from '../../../util/eventPrices';
import {
  groupColorKeys, normalizeSelfServiceBooking, normalizeSignupChannel, normalizeValidityMode, ROLE_COLOR_CHOICES,
} from '../../../util/participantGroups';

// Entity limits (AveOrgaDbContext: Name/PluralName 100, Description 500, SelfServiceIntro 2000).
export const NAME_MAX = 100;
export const DESCRIPTION_MAX = 500;
export const INTRO_MAX = 2000;
export const PRICE_MAX = 10000;

export const TAB = {
  general: 'general',
  membership: 'membership',
  signup: 'signup',
  prices: 'prices',
  mails: 'mails',
};

export const TAB_LABEL = {
  general: 'Allgemein',
  membership: 'Mitgliedschaft',
  signup: 'Anmeldung',
  prices: 'Preise',
  mails: 'Mails',
};

// Guest has no memberships → no "Mitgliedschaft" tab.
export const editorTabs = (isGuest) => (isGuest
  ? [TAB.general, TAB.signup, TAB.prices, TAB.mails]
  : [TAB.general, TAB.membership, TAB.signup, TAB.prices, TAB.mails]);

// Field → tab that shows it (jump there when saving fails validation).
export const ERROR_TAB = {
  name: TAB.general,
  pluralName: TAB.general,
  description: TAB.general,
  selfServiceIntro: TAB.signup,
  defaultEntryPrice: TAB.prices,
};

// Only the entry price — add-on prices (e.g. Leihausrüstung) live in the catalog / per process.
const PRICE_FIELDS = ['defaultEntryPrice'];

// Saved group → editor form (all strings/booleans, prices as input strings "20.00").
export function formFromGroup(group) {
  return {
    name: group?.name ?? '',
    // normalizeParticipantGroup fills an empty plural with the name; prefer the raw value once it is kept.
    pluralName: (group && 'rawPluralName' in group ? group.rawPluralName : group?.pluralName) ?? '',
    description: group?.description ?? '',
    colorKey: group?.colorKey ?? 'neutral',
    validityMode: normalizeValidityMode(group?.validityMode),
    signupChannel: normalizeSignupChannel(group?.signupChannel),
    selfServiceBooking: normalizeSelfServiceBooking(group?.selfServiceBooking),
    selfServiceAddOns: group?.selfServiceAddOns === true,
    selfServiceIntro: group?.selfServiceIntro ?? '',
    defaultEntryPrice: toPriceInput(group?.defaultEntryPrice),
    copyFromId: '',
  };
}

// New group: first palette color neither an active group nor a role uses yet, entry price empty (must be
// decided consciously).
export function createDefaults(groups) {
  const used = new Set((groups ?? []).filter(g => !g.isArchived).map(g => g.colorKey));
  const offered = groupColorKeys().filter(k => k !== 'neutral');
  const colorKey = offered.find(k => !used.has(k)) ?? offered[0] ?? 'neutral';
  return {
    ...formFromGroup(null),
    colorKey,
    defaultEntryPrice: '',
  };
}

// Prices compare by value ("20" equals "20.00"), everything else strictly.
export function sameFormValue(key, a, b) {
  if (PRICE_FIELDS.includes(key)) {
    const pa = parsePriceInput(a);
    const pb = parsePriceInput(b);
    if (pa == null || pb == null || Number.isNaN(pa) || Number.isNaN(pb)) return String(a ?? '') === String(b ?? '');
    return samePrice(pa, pb);
  }
  return a === b;
}

export const sameForm = (a, b) => Object.keys(a).every(key => sameFormValue(key, a[key], b[key]));

// The saved default entry price differs from the form (→ "Auf zukünftige Spieltage anwenden" must wait for saving).
export const pricesDirty = (form, baseline) =>
  PRICE_FIELDS.some(key => !sameFormValue(key, form[key], baseline[key]));

function priceError(value) {
  const n = parsePriceInput(value);
  if (n == null || Number.isNaN(n)) return 'Bitte einen gültigen Preis angeben (0 = kostenlos).';
  if (n > PRICE_MAX) return `Höchstens ${PRICE_MAX.toLocaleString('de-DE')} €.`;
  return null;
}

/**
 * Field errors ({ field: message }); empty object = valid.
 * ctx: { groups (all), groupId (null when creating), copySource (group whose prices are taken over) }
 */
export function validateForm(form, { groups, groupId, copySource }) {
  const errors = {};
  const name = form.name.trim();
  if (!name) {
    errors.name = 'Bitte einen Namen angeben.';
  } else if (name.length > NAME_MAX) {
    errors.name = `Der Name darf höchstens ${NAME_MAX} Zeichen lang sein.`;
  } else if ((groups ?? []).some(g => g.id !== groupId && g.name.trim().toLowerCase() === name.toLowerCase())) {
    errors.name = `Eine Gruppe „${name}“ existiert bereits.`;
  }
  if (form.pluralName.trim().length > NAME_MAX) {
    errors.pluralName = `Die Mehrzahl darf höchstens ${NAME_MAX} Zeichen lang sein.`;
  }
  if (form.description.trim().length > DESCRIPTION_MAX) {
    errors.description = `Die Beschreibung darf höchstens ${DESCRIPTION_MAX} Zeichen lang sein.`;
  }
  if (form.selfServiceIntro.trim().length > INTRO_MAX) {
    errors.selfServiceIntro = `Der Hinweistext darf höchstens ${INTRO_MAX} Zeichen lang sein.`;
  }
  // Copied prices come from the source group (the API overrides the request values).
  if (!copySource) {
    PRICE_FIELDS.forEach(key => {
      const message = priceError(form[key]);
      if (message) errors[key] = message;
    });
  }
  return errors;
}

/** Editor form → SaveParticipantGroupRequest. The icon is kept untouched (no icon editor yet). */
export function toSaveRequest(form, group, copySource) {
  const isGuest = group?.isGuest === true;
  const priceOf = (key) => (copySource ? Number(copySource[key] ?? 0) : parsePriceInput(form[key]));
  return {
    name: form.name.trim(),
    pluralName: form.pluralName.trim() || null,
    description: form.description.trim() || null,
    colorKey: form.colorKey,
    icon: group?.icon ?? null,
    validityMode: form.validityMode,
    signupChannel: isGuest ? 'Form' : form.signupChannel,
    selfServiceBooking: form.selfServiceBooking,
    selfServiceAddOns: form.selfServiceAddOns,
    // Keep inner line breaks; the API trims and stores null for blank text.
    selfServiceIntro: form.selfServiceIntro.trim() ? form.selfServiceIntro : null,
    defaultEntryPrice: priceOf('defaultEntryPrice'),
  };
}

// Error text for popups/inline boxes: the API answers 400/409 with a plain string.
export function apiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.title) return data.title;
  if (!err?.response) return 'Keine Verbindung – bitte später erneut versuchen.';
  if (err.response.status === 404) return 'Die Gruppe existiert nicht mehr.';
  return fallback;
}

// Local date as yyyy-MM-dd (input type="date", API DateOnly).
export function todayIso(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// "2027-04-12" → "12.04.2027"
export function formatDateDe(iso) {
  const [y, m, d] = String(iso ?? '').split('-');
  return y && m && d ? `${d.substring(0, 2)}.${m}.${y}` : String(iso ?? '');
}

// "12 Mitglieder" / "1 Mitglied" — the group's own nouns read naturally in the list.
export function memberCountLabel(group) {
  const n = Number(group?.currentMemberCount ?? 0);
  return `${n} ${n === 1 ? group.name : group.pluralName}`;
}

// ── Roles (section "Rollen": RoleSetting, PUT /Role/UpdateSettings/{role}) ─────────────────────────

// Entity limit (RoleSetting.Name max 50).
export const ROLE_NAME_MAX = 50;

// List meta of a role: "Leitet Spieltage · 85,00 € Lohn" / "Leitet keine Spieltage".
export function roleLeadSummary(setting) {
  if (!setting?.canLeadEvents) return 'Leitet keine Spieltage';
  return `Leitet Spieltage · ${formatEuro(setting.leaderSalary)} Lohn`;
}

// Saved setting → editor form (salary as input string "85.00").
export function roleFormFromSetting(setting) {
  return {
    name: setting?.name ?? '',
    colorKey: setting?.colorKey ?? 'negative',
    canLeadEvents: setting?.canLeadEvents !== false,
    leaderSalary: toPriceInput(setting?.leaderSalary ?? 0),
  };
}

// The salary compares by value like the group prices ("85" equals "85.00").
export function sameRoleFormValue(key, a, b) {
  return key === 'leaderSalary' ? sameFormValue('defaultEntryPrice', a, b) : a === b;
}

export const sameRoleForm = (a, b) => Object.keys(a).every(key => sameRoleFormValue(key, a[key], b[key]));

/**
 * Field errors ({ field: message }); empty object = valid.
 * otherRoles: the other role settings (names must stay distinguishable in labels like "Orga · Mitglied").
 * The salary is only checked while the role can lead events (otherwise the saved value is kept).
 */
export function validateRoleForm(form, otherRoles) {
  const errors = {};
  const name = form.name.trim();
  if (!name) {
    errors.name = 'Bitte einen Anzeigenamen angeben.';
  } else if (name.length > ROLE_NAME_MAX) {
    errors.name = `Der Anzeigename darf höchstens ${ROLE_NAME_MAX} Zeichen lang sein.`;
  } else if ((otherRoles ?? []).some(r => r.name.trim().toLowerCase() === name.toLowerCase())) {
    errors.name = `Die Rolle „${name}“ existiert bereits.`;
  }
  if (!ROLE_COLOR_CHOICES.includes(form.colorKey)) {
    errors.colorKey = 'Bitte eine Farbe wählen.';
  }
  if (form.canLeadEvents) {
    const salary = parsePriceInput(form.leaderSalary);
    if (salary == null || Number.isNaN(salary)) errors.leaderSalary = 'Bitte einen gültigen Betrag angeben (0 = kein Lohn).';
    else if (salary > PRICE_MAX) errors.leaderSalary = `Höchstens ${PRICE_MAX.toLocaleString('de-DE')} €.`;
  }
  return errors;
}

// Editor form → SaveRoleSettingRequest. A role that cannot lead keeps its saved salary.
export function toRoleSaveRequest(form, setting) {
  return {
    name: form.name.trim(),
    colorKey: form.colorKey,
    canLeadEvents: form.canLeadEvents,
    leaderSalary: form.canLeadEvents ? parsePriceInput(form.leaderSalary) : Number(setting?.leaderSalary ?? 0),
  };
}
